import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { findAnswerPost, ANSWER_TRADE_LABEL, type AnswerPost, type AnswerSection } from "@/lib/answers-data";
import { BUSINESS } from "@/lib/site-data";
import { AnswerBlock } from "@/components/AnswerBlock";

const BASE_URL = "https://lehitradessaeo.com";

export const Route = createFileRoute("/answers/$slug")({
  loader: ({ params }) => {
    const post = findAnswerPost(params.slug);
    if (!post) throw notFound();
    return { post } as { post: AnswerPost };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Answer not found" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }
    const p = loaderData.post;
    const title = `${p.title} | ${BUSINESS.shortName}`;
    const canonical = `${BASE_URL}/answers/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: p.description },
        { property: "og:title", content: p.title },
        { property: "og:description", content: p.description },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: p.title },
        { name: "twitter:description", content: p.description },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: p.title,
            description: p.description,
            author: { "@type": "Organization", name: BUSINESS.name },
            publisher: { "@type": "Organization", name: BUSINESS.name },
            mainEntityOfPage: canonical,
            keywords: p.keyword,
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["[data-speakable=\"question\"]", "[data-speakable=\"answer\"]"],
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-3xl font-black">Answer not found</h1>
      <p className="mt-2 text-muted-foreground">
        <Link to="/answers" search={{ page: 1, trade: "" }} className="underline">Browse all answers</Link>.
      </p>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-3xl font-black">Something went wrong</h1>
      <p className="mt-2 text-muted-foreground">
        <Link to="/answers" search={{ page: 1, trade: "" }} className="underline">Browse all answers</Link>.
      </p>
    </main>
  ),
  component: AnswerPostPage,
});

function AnswerPostPage() {
  const { post } = Route.useLoaderData() as { post: AnswerPost };
  const tradeLabel = ANSWER_TRADE_LABEL[post.tradeSlug] ?? post.tradeSlug;
  return (
    <main className="mx-auto max-w-3xl px-6 py-20">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
        <Link to="/answers" search={{ page: 1, trade: post.tradeSlug }} className="hover:text-foreground">
          {tradeLabel}
        </Link>{" "}
        · {post.readMinutes} min read
      </p>
      <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">{post.title}</h1>
      <p className="mt-5 text-lg text-muted-foreground">{post.excerpt}</p>

      <AnswerBlock question={post.title} answer={post.tldr} />

      <div className="mt-12 space-y-10">
        {post.sections.map((section: AnswerSection, si: number) => (
          <section key={si}>
            <h2 className="font-display text-2xl tracking-tight">{section.heading}</h2>
            {section.paragraphs.map((para: string, idx: number) => (
              <p key={idx} className="mt-4 leading-relaxed text-muted-foreground">{para}</p>
            ))}
            {section.bullets && section.bullets.length > 0 && (
              <ul className="mt-4 space-y-2">
                {section.bullets.map((b: string, bi: number) => (
                  <li key={bi} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-sm">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    {b}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <div className="mt-16 rounded-2xl border border-border bg-card p-8 text-center">
        <h3 className="font-display text-2xl tracking-tight">Want this run for your business?</h3>
        <p className="mt-3 text-muted-foreground">Free local SEO audit. No pitch deck, no contract pressure.</p>
        <Link
          to="/contact"
          className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Get my free audit
        </Link>
      </div>
    </main>
  );
}